/**
 * Checkout API helpers - payload builders and response handlers
 * Pure functions that can be tested without fetch mocking
 */

import { Product, GuestAddress, ShippingRate, PaymentIntentPayload } from './checkout-types';
import { isProductShippable } from './checkout-validation';

/**
 * Build payment intent payload for authenticated checkout
 */
export function buildAuthenticatedPaymentIntentPayload(
  product: Product,
  paymentMethodId: string | undefined,
  shippingAddress: { id?: string | number } | null,
  shippingRate: ShippingRate | null
): PaymentIntentPayload {
  return {
    mode: 'authenticated',
    listingId: Number(product.id),
    paymentMethodId,
    shippingAddressId: shippingAddress?.id ? Number(shippingAddress.id) : undefined,
    listing: {
      id: Number(product.id),
      title: String(product.title || ''),
      price: Number(product.price || 0),
      shippable: isProductShippable(product),
      user: product.user?.id ? { id: Number(product.user.id) } : undefined,
    },
    selectedShippingRate: shippingRate ? {
      id: shippingRate.id,
      provider: shippingRate.provider?.name || String(shippingRate.provider),
      serviceName: shippingRate.service,
      amount: Number(shippingRate.amount),
      estimatedDays: Number(shippingRate.estimatedDays),
    } : undefined,
  };
}

/**
 * Build payment intent payload for guest checkout
 */
export function buildGuestPaymentIntentPayload(
  product: Product,
  email: string,
  shippingAddress: GuestAddress,
  shippingRate: ShippingRate | null
): PaymentIntentPayload {
  return {
    mode: 'guest',
    listingId: Number(product.id),
    email,
    shippingAddress,
    listing: {
      id: Number(product.id),
      title: String(product.title || ''),
      price: Number(product.price || 0),
      shippable: isProductShippable(product),
      user: product.user?.id ? { id: Number(product.user.id) } : undefined,
    },
    selectedShippingRate: shippingRate ? {
      id: shippingRate.id,
      provider: shippingRate.provider?.name || String(shippingRate.provider),
      serviceName: shippingRate.service,
      amount: Number(shippingRate.amount),
      estimatedDays: Number(shippingRate.estimatedDays),
    } : undefined,
  };
}

/**
 * Parse listing status error message
 */
export function getListingStatusMessage(status: number | string): string {
  // PURCHASED (3) and HELD (4) are both a sale in progress.
  if (status === 3 || status === '3' || status === 4 || status === '4') {
    return 'This item has a sale pending and is no longer available for checkout.';
  }
  return 'This item is no longer available for purchase.';
}

/**
 * Check if error indicates seller Stripe setup issue
 */
export function isSellerSetupError(errorMessage: string | undefined): boolean {
  if (!errorMessage) return false;
  return (
    errorMessage.includes('Seller has not completed payout account setup') ||
    errorMessage.includes('not completed payout account') ||
    errorMessage.includes('Stripe Connect')
  );
}

/**
 * Payment intent status checks
 */
export function isPaymentSuccessStatus(status: string | undefined): boolean {
  return status === 'succeeded' || status === 'processing' || status === 'requires_capture';
}

export function isPaymentRequiresConfirmation(status: string | undefined): boolean {
  return status === 'requires_confirmation';
}

/**
 * Build Google Analytics purchase event data
 */
export function buildPurchaseEventData(
  product: Product,
  paymentIntentId: string,
  shippingAmount: number
): {
  transaction_id: string;
  affiliation: number | string | undefined;
  currency: string;
  value: number;
  shipping: number;
  items: Array<{
    item_id: number | string;
    item_name: string | undefined;
    affiliation: number | string | undefined;
    currency: string;
    index: number;
    item_category: number | undefined;
    item_category2: number | undefined;
    price: number | undefined;
    quantity: number;
  }>;
} {
  return {
    transaction_id: paymentIntentId,
    affiliation: product.user?.id,
    currency: 'USD',
    value: (product.price || 0) * 0.1,
    shipping: shippingAmount,
    items: [{
      item_id: product.id,
      item_name: product.title,
      affiliation: product.user?.id,
      currency: 'USD',
      index: 0,
      item_category: product.categoryGroupId,
      item_category2: product.categoryId,
      price: product.price,
      quantity: 1,
    }],
  };
}

/**
 * Build Google Analytics begin_checkout event data
 */
export function buildBeginCheckoutEventData(product: Product): {
  currency: string;
  value: number;
  items: Array<{
    item_id: number | string;
    item_name: string | undefined;
    affiliation: number | string | undefined;
    currency: string;
    index: number;
    item_category: number | undefined;
    item_category2: number | undefined;
    price: number | undefined;
    quantity: number;
  }>;
} {
  return {
    currency: 'USD',
    value: (product.price || 0) * 0.1,
    items: [{
      item_id: product.id,
      item_name: product.title,
      affiliation: product.user?.id,
      currency: 'USD',
      index: 0,
      item_category: product.categoryGroupId,
      item_category2: product.categoryId,
      price: product.price,
      quantity: 1,
    }],
  };
}

/**
 * Extract error message from various error types
 */
export function extractErrorMessage(error: unknown, defaultMessage: string = 'An error occurred'): string {
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message) || defaultMessage;
  }
  if (error && typeof error === 'object' && 'toString' in error && typeof error.toString === 'function') {
    const str = error.toString() as string;
    if (str !== '[object Object]') {
      return str;
    }
  }
  return defaultMessage;
}
